import { Minus, Plus, Scan } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

import { capabilityRank } from "../../domain/capability";
import { connectedNodeIds } from "../../domain/graph";
import type { GraphEdge, GraphMode, TechnologyNode } from "../../domain/model";

interface Props {
  nodes: TechnologyNode[];
  edges: GraphEdge[];
  selectedId: string | null;
  mode: GraphMode;
  onSelect: (node: TechnologyNode) => void;
  compact?: boolean;
}

const nodeWidth = 142;
const nodeHeight = 54;

export function GraphCanvas({ nodes, edges, selectedId, mode, onSelect, compact = false }: Props) {
  const [view, setView] = useState({ x: 0, y: 0, width: 1260, height: 1160 });
  const pointer = useRef<{ x: number; y: number; viewX: number; viewY: number } | null>(null);
  const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const connected = useMemo(() => selectedId ? connectedNodeIds(selectedId, edges) : null, [selectedId, edges]);
  const nodeSetKey = nodes.map((node) => node.id).join("|");

  useEffect(() => {
    if (!nodes.length) return;
    const minX = Math.min(...nodes.map((node) => node.position.x)) - 90;
    const maxX = Math.max(...nodes.map((node) => node.position.x)) + nodeWidth + 90;
    const minY = Math.min(...nodes.map((node) => node.position.y)) - 80;
    const maxY = Math.max(...nodes.map((node) => node.position.y)) + nodeHeight + 80;
    setView({ x: Math.max(0, minX), y: Math.max(0, minY), width: Math.max(420, maxX - minX), height: Math.max(360, maxY - minY) });
    // Reset framing only when the visible node set changes, not on selection.
  }, [nodeSetKey]);

  function zoom(factor: number) {
    setView((current) => {
      const width = Math.min(1600, Math.max(520, current.width * factor));
      const height = width * (1160 / 1260);
      return { x: current.x + (current.width - width) / 2, y: current.y + (current.height - height) / 2, width, height };
    });
  }

  function onPointerDown(event: PointerEvent<SVGSVGElement>) {
    if (compact || event.button !== 0 || (event.target as Element).closest(".graph-node")) return;
    pointer.current = { x: event.clientX, y: event.clientY, viewX: view.x, viewY: view.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: PointerEvent<SVGSVGElement>) {
    if (!pointer.current) return;
    const scale = view.width / event.currentTarget.clientWidth;
    setView((current) => ({ ...current, x: pointer.current!.viewX - (event.clientX - pointer.current!.x) * scale, y: pointer.current!.viewY - (event.clientY - pointer.current!.y) * scale }));
  }

  function onPointerUp(event: PointerEvent<SVGSVGElement>) {
    pointer.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function nodeKey(event: KeyboardEvent<SVGGElement>, node: TechnologyNode) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(node);
    }
  }

  const layerBands = Array.from(new Set(nodes.map((node) => node.layer))).map((layer) => {
    const layerNodes = nodes.filter((node) => node.layer === layer);
    return { layer, y: Math.min(...layerNodes.map((node) => node.position.y)) - 32 };
  });

  return (
    <div className={compact ? "graph-stage compact" : "graph-stage"}>
      {!compact && (
        <div className="zoom-tools" aria-label="Graph zoom">
          <button className="icon-button" onClick={() => zoom(0.82)} aria-label="Zoom in"><Plus /></button>
          <button className="icon-button" onClick={() => zoom(1.18)} aria-label="Zoom out"><Minus /></button>
          <button className="icon-button" onClick={() => setView({ x: 0, y: 0, width: 1260, height: 1160 })} aria-label="Reset view"><Scan /></button>
        </div>
      )}
      <svg
        className="system-graph"
        viewBox={`${view.x} ${view.y} ${view.width} ${view.height}`}
        role="group"
        aria-label={`Full-stack civilization graph with ${nodes.length} visible nodes in ${mode} mode`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <defs>
          <pattern id="micro-grid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.13" />
          </pattern>
          <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" /></marker>
        </defs>
        <rect x="0" y="0" width="1260" height="1160" className="graph-grid" />
        {layerBands.map((band) => <g key={band.layer} className="layer-band"><line x1="22" x2="1238" y1={band.y} y2={band.y} /><text x="28" y={band.y - 8}>{band.layer.toUpperCase()}</text></g>)}
        <g className="graph-edges">
          {edges.map((item) => {
            const source = byId.get(item.sourceId);
            const target = byId.get(item.targetId);
            if (!source || !target) return null;
            const active = !connected || (connected.has(item.sourceId) && connected.has(item.targetId));
            return <line key={item.id} x1={source.position.x + nodeWidth / 2} y1={source.position.y + nodeHeight / 2} x2={target.position.x + nodeWidth / 2} y2={target.position.y + nodeHeight / 2} className={active ? "active" : "muted"} markerEnd="url(#arrow)" />;
          })}
        </g>
        <g className="graph-nodes">
          {nodes.map((node) => {
            const selected = node.id === selectedId;
            const active = !connected || connected.has(node.id);
            const level = capabilityRank[node.capabilityState];
            return (
              <g
                key={node.id}
                className={`graph-node state-${node.capabilityState.toLowerCase()} ${selected ? "selected" : ""} ${active ? "active" : "muted"}`}
                transform={`translate(${node.position.x} ${node.position.y})`}
                role="button"
                tabIndex={compact ? -1 : 0}
                aria-label={`${node.name}, ${node.capabilityState}`}
                onClick={(event) => { event.stopPropagation(); onSelect(node); }}
                onKeyDown={(event) => nodeKey(event, node)}
              >
                <rect width={nodeWidth} height={nodeHeight} rx="3" />
                <path d={`M 0 0 H ${Math.max(18, 20 + level * 15)} V 4 H 0 Z`} />
                <text className="node-index" x="10" y="17">0{level + 1}</text>
                <text className="node-name" x="10" y="36">{node.name}</text>
                <text className="node-state" x={nodeWidth - 8} y="16" textAnchor="end">{node.capabilityState}</text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
